import {
  CSSProperties,
  HTMLAttributes,
  InputHTMLAttributes,
  useEffect,
  useRef,
} from 'react'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { colors } from '../constants/colors'

import { Flex } from './Flex'

const focusBorderAnimation = keyframes`
  0% {
    border-color: ${colors.darkBlue2};
    box-shadow: 0 0 0 0 rgba(0, 125, 255, 0);
  }
  100% {
    border-color: ${colors.blue};
    box-shadow: 0 0 0 4px rgba(0, 125, 255, 0.15);
  }
`

const errorShakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`

const fontStyles = css`
  color: ${colors.white};
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
`

const sharedStyles = css`
  display: flex;
  ${fontStyles};
  padding: 1.25rem;
  width: 100%;
  align-items: center;
  border: 1px solid ${colors.darkBlue2};
  border-radius: 16px;
  background-color: ${colors.darkBlue2};
  outline: none;

  &:focus {
    outline: none;
  }
`

const activeSharedStyled = css`
  cursor: pointer;
  transition: all 0.2s ease-out;

  &:focus:not(&:disabled) {
    border: 1px solid ${colors.blue};
    animation: ${focusBorderAnimation} 0.3s ease-out forwards;
  }

  &::placeholder {
    color: ${colors.light2};
    opacity: 1;
    transition:
      transform 0.25s ease,
      opacity 0.25s ease;
  }

  &:focus::placeholder {
    opacity: 0.7;
    transform: translateY(-5px) scale(0.9);
  }

  &::-ms-input-placeholder {
    color: ${colors.light2};
  }
`

export const Input = styled.input<{ disabled?: boolean }>`
  ${sharedStyles}
  ${activeSharedStyled}
  height: 64px;
`

export const StyledInput = styled.input`
  ${fontStyles};
  border: 0;
  background-color: transparent;
  width: 100%;

  &:disabled {
    color: ${colors.light1};
  }

  &::placeholder {
    color: ${colors.light2};
  }

  &:focus {
    outline: none;
  }
`

export interface InputWithIconsProps
  extends InputHTMLAttributes<HTMLInputElement> {
  iconRight?: JSX.Element
  iconLeft?: JSX.Element
  hasError?: boolean
}

export function InputWithIcons({
  iconRight,
  iconLeft,
  hasError,
  ...inputProps
}: InputWithIconsProps) {
  const ref = useRef<HTMLInputElement>(null)

  return (
    <CustomInputWrapper
      hasError={hasError}
      onClick={() => ref.current?.focus()}
      disabled={inputProps.disabled}
    >
      {iconLeft}
      <StyledInput ref={ref} {...inputProps} />
      {iconRight}
    </CustomInputWrapper>
  )
}

interface NumberInputWithIconsProps extends NumericFormatProps {
  iconRight?: JSX.Element
  iconLeft?: JSX.Element
  hasError?: boolean
}

export function NumberInputWithIcons({
  iconRight,
  iconLeft,
  hasError,
  ...inputProps
}: NumberInputWithIconsProps) {
  const ref = useRef<HTMLInputElement>(null)

  return (
    <CustomInputWrapper
      hasError={hasError}
      onClick={() => ref.current?.focus()}
      disabled={inputProps.disabled}
    >
      {iconLeft}
      <NumericFormat
        customInput={StyledInput}
        getInputRef={ref}
        {...inputProps}
      />
      {iconRight}
    </CustomInputWrapper>
  )
}

const errorShakeAnimationStyle = css`
  animation: ${errorShakeAnimation} 0.4s ease-in-out;
`

// use contentEditable to achieve auto-grow
export const TextArea = styled.textarea<{
  placeholder?: string
  hasError?: boolean
}>`
  ${sharedStyles}
  ${activeSharedStyled}
  overflow: hidden;
  min-height: 20rem;
  resize: none;
  word-break: break-all;
  overflow-wrap: normal;
  padding-right: 22px;
  padding-bottom: 22px;
  transition:
    border 0.25s ease-out,
    box-shadow 0.25s ease-out,
    transform 0.1s ease-out;

  &:active:not(&:disabled) {
    transform: scale(0.995);
  }

  &:focus:not(&:disabled) {
    box-shadow: 0 0 0 4px rgba(0, 125, 255, 0.15);
  }

  &:empty:before {
    content: '${({ placeholder = '' }) => placeholder}';
    color: ${colors.light2};
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  &:focus:empty:before {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
  }

  ${({ hasError }) =>
    hasError &&
    `
    border: 1px solid ${colors.red};
    ${errorShakeAnimationStyle};
    
    &:focus:not(&:disabled) {
      box-shadow: 0 0 0 4px rgba(255, 69, 0, 0.15);
    }
  `}
`

interface CustomInputProps extends HTMLAttributes<HTMLDivElement> {
  leftElement?: JSX.Element
  rightElement?: JSX.Element
  wrapperStyle?: CSSProperties
  inputType?: 'string' | 'number'
  defaultValue?: string
  hasError?: boolean
  disabled?: boolean
}

const CustomInputWrapper = styled(Flex)<{
  disabled?: boolean
  hasError?: boolean
}>`
  ${sharedStyles};
  font-weight: 800;
  ${({ disabled }) => !disabled && activeSharedStyled}
  height: 64px;

  transition:
    border 0.25s ease-out,
    box-shadow 0.25s ease-out,
    transform 0.1s ease-out;
  position: relative;
  overflow: hidden;

  ${({ hasError }) =>
    hasError &&
    `
    border: 1px solid ${colors.red};
    ${errorShakeAnimationStyle};
    `}

  &:hover {
    border: 1px solid
      ${({ hasError }) => (hasError ? colors.red : colors.darkBlue4)};
  }

  &:not(:disabled):focus-within {
    border: 1px solid ${({ hasError }) => (hasError ? colors.red : colors.blue)};
    box-shadow: 0 0 0 4px
      ${({ hasError }) =>
        hasError ? 'rgba(255, 69, 0, 0.15)' : 'rgba(0, 125, 255, 0.15)'};
  }

  &:after:not(:disabled) {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image: radial-gradient(
      circle,
      ${colors.blue} 10%,
      transparent 10.01%
    );
    background-repeat: no-repeat;
    background-position: 50%;
    transform: scale(10, 10);
    opacity: 0;
    transition:
      transform 0.5s,
      opacity 0.8s;
  }

  &:active:not(:disabled):after {
    transform: scale(0, 0);
    opacity: 0.2;
    transition: 0s;
  }
`

const DivInput = styled.div`
  min-width: 0.5rem;
  &:focus {
    outline: none;
  }
`

export default function CustomInput({
  leftElement,
  rightElement,
  wrapperStyle,
  inputType = 'string',
  defaultValue = '',
  hasError,
  ...inputProps
}: CustomInputProps) {
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!ref.current) return
    ref.current.innerText = defaultValue
  }, [defaultValue])

  return (
    <CustomInputWrapper
      hasError={hasError}
      style={wrapperStyle}
      onClick={() => ref.current?.focus()}
      disabled={inputProps.disabled}
    >
      {leftElement}
      <DivInput
        role="textbox"
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        {...inputProps}
        onInput={(e) => {
          if (inputType === 'number') {
            const validNumberPattern = /^[0-9]*$/
            const inputValue = e.currentTarget.innerText

            if (validNumberPattern.test(inputValue)) {
              inputProps.onChange?.(e)
            } else {
              e.preventDefault()
              e.currentTarget.innerText = inputValue.slice(0, -1)
            }
          }

          inputProps.onChange?.(e)
        }}
      />
      {rightElement}
    </CustomInputWrapper>
  )
}
